/**
 * @fileoverview Manages API key creation, display, and revocation using the `ApiKeyManager` component. It utilizes the `useApiKeys` hook to fetch API keys and interacts with `createApiKey` and `revokeApiKey` functions. The component includes `CreateKeyForm` and `NewKeyDisplay` components for key management and configuration snippets.
 */

import { Timestamp } from 'firebase/firestore'
import React, { useState, useRef, useEffect } from 'react'
import { createApiKey, revokeApiKey } from '../../firebase/functions'
import { useApiKeys } from '../../hooks/useUserData'
import { useAuth } from '../../hooks/useAuth'
import { useToast } from '../../hooks/useToast'
import { ToastContainer } from '../Toast/ToastContainer'
import { installRefInCursor, McpMode } from '../../utils/cursorDeeplink'
import Button from '../Button'
import '../../styles/components/ApiKeyManager.css'
import PageContainer from '../PageContainer'

// SVG Icon Components
const EyeOffIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ verticalAlign: 'middle' }}
  >
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
    <circle cx="12" cy="12" r="3"></circle>
  </svg>
)

const EyeIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ verticalAlign: 'middle' }}
  >
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
    <line x1="1" y1="1" x2="23" y2="23"></line>
  </svg>
)

const CopyIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ verticalAlign: 'middle' }}
  >
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
  </svg>
)

const CursorIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ verticalAlign: 'middle' }}
  >
    {/* Simple isometric cube */}
    <path d="M12 2l8 4.5v9L12 20l-8-4.5v-9L12 2z" />
    <path d="M12 2v18" />
    <path d="M4 6.5l8 4.5 8-4.5" />
  </svg>
)

interface CreateKeyFormProps {
  newKeyName: string
  setNewKeyName: (name: string) => void
  handleCreateApiKey: (e: React.FormEvent) => Promise<void>
  isCreating: boolean
  setShowCreateForm: (show: boolean) => void
  inputRef: React.RefObject<HTMLInputElement | null>
}

const CreateKeyForm: React.FC<CreateKeyFormProps> = ({
  newKeyName,
  setNewKeyName,
  handleCreateApiKey,
  isCreating,
  setShowCreateForm,
  inputRef,
}) => {
  return (
    <form onSubmit={handleCreateApiKey} className="create-key-form">
      <input
        ref={inputRef}
        type="text"
        value={newKeyName}
        onChange={(e) => setNewKeyName(e.target.value)}
        placeholder="Enter a name for your API key"
        required
        className="key-name-input"
      />
      <div style={{ display: 'flex', gap: '0.5rem' }}>
        <Button type="submit" variant="primary" size="medium" disabled={isCreating}>
          {isCreating ? 'Creating...' : 'Create'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          size="medium"
          onClick={() => setShowCreateForm(false)}
        >
          Cancel
        </Button>
      </div>
    </form>
  )
}

interface NewKeyDisplayProps {
  newKey: { name: string; key: string }
  activeTab: string
  setActiveTab: (tab: string) => void
  handleCopyApiKey: (key: string, buttonElement?: HTMLElement) => void
  getStdioConfig: (key: string) => string
  getHttpConfig: (key: string) => string
}

const NewKeyDisplay: React.FC<NewKeyDisplayProps> = ({
  newKey,
  activeTab,
  setActiveTab,
  handleCopyApiKey,
  getStdioConfig,
  getHttpConfig,
}) => {
  return (
    <div className="new-key-alert">
      <div className="new-key-header">
        <h3>{newKey.name}</h3>
      </div>
      <div className="key-value">
        {newKey.key}
        <Button
          onClick={(e) => handleCopyApiKey(newKey.key, e.currentTarget)}
          variant="primary"
          size="small"
        >
          <CopyIcon />
        </Button>
      </div>

      <div className="mcp-config-tabs">
        <div className="tabs-header">
          <button
            className={`tab-button ${activeTab === 'http' ? 'active' : ''}`}
            onClick={() => setActiveTab('http')}
          >
            MCP Streamable HTTP
          </button>
          <button
            className={`tab-button ${activeTab === 'stdio' ? 'active' : ''}`}
            onClick={() => setActiveTab('stdio')}
          >
            MCP stdio
          </button>
        </div>
        <div className="tab-content">
          <pre className="code-block">
            {activeTab === 'stdio' ? getStdioConfig(newKey.key) : getHttpConfig(newKey.key)}
          </pre>
          <div className="config-actions">
            <Button
              onClick={(e) =>
                handleCopyApiKey(
                  activeTab === 'stdio' ? getStdioConfig(newKey.key) : getHttpConfig(newKey.key),
                  e.currentTarget,
                )
              }
              variant="secondary"
              size="small"
            >
              <CopyIcon />
            </Button>
            <Button
              onClick={() =>
                installRefInCursor(newKey.key, activeTab === 'stdio' ? McpMode.STDIO : McpMode.HTTP)
              }
              variant="secondary"
              size="small"
              title="Install in Cursor"
            >
              <CursorIcon /> Install in Cursor
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export const ApiKeyManager = () => {
  const { apiKeys, loading } = useApiKeys()
  const { user } = useAuth()
  const { toasts, removeToast, showMessage } = useToast()
  const [newKeyName, setNewKeyName] = useState('')
  const [newKey, setNewKey] = useState<{ name: string; key: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [visibleKeys, setVisibleKeys] = useState<Record<string, boolean>>({})
  const [activeTab, setActiveTab] = useState('http')
  const inputRef = useRef<HTMLInputElement>(null)

  const anyActiveKeys = apiKeys.some((key) => key.isActive)

  useEffect(() => {
    if (showCreateForm && inputRef.current) {
      inputRef.current.focus()
    }
  }, [showCreateForm])

  // Always show the first active key setup
  useEffect(() => {
    if (!loading && apiKeys.length > 0 && !newKey) {
      const firstActiveKey = apiKeys.find((key) => key.isActive)
      if (firstActiveKey) {
        setNewKey({ name: firstActiveKey.name, key: firstActiveKey.key })
      }
    }
  }, [apiKeys, loading, newKey])

  const showKeySetup = (key: any) => {
    setNewKey({ name: key.name, key: key.key })
  }

  const toggleKeyVisibility = (keyId: string, key: any) => {
    const isCurrentlyVisible = visibleKeys[keyId]
    setVisibleKeys((prev) => ({
      ...prev,
      [keyId]: !prev[keyId],
    }))

    // When showing a key, also display its setup configuration
    if (!isCurrentlyVisible) {
      showKeySetup(key)
    }
  }

  const handleCreateApiKey = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!newKeyName.trim()) {
      setError('API key name is required')
      return
    }

    setIsCreating(true)
    setError(null)

    try {
      const result = await createApiKey({ name: newKeyName })
      const data = result.data as any
      setNewKey({ name: data.name, key: data.key })
      setNewKeyName('')
      setShowCreateForm(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    } finally {
      setIsCreating(false)
    }
  }

  const handleRevokeApiKey = async (keyId: string) => {
    if (!confirm('Are you sure you want to revoke this API key? This action cannot be undone.')) {
      return
    }

    try {
      await revokeApiKey({ keyId })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred')
    }
  }

  const handleCopyApiKey = (key: string, buttonElement?: HTMLElement) => {
    navigator.clipboard
      .writeText(key)
      .then(() => showMessage('Copied to clipboard', 2000, buttonElement))
      .catch(() => showMessage('Failed to copy', 2000, buttonElement))
  }

  const truncateKey = (key: string) => {
    return `${key.substring(0, 8)}...${key.substring(key.length - 4)}`
  }

  const getStdioConfig = (key: string) => {
    return `{
  "Ref": {
    "command": "npx",
    "args": ["ref-tools-mcp@latest"],
    "env": {
      "REF_API_KEY": "${key}"
    }
  }
}`
  }

  const getHttpConfig = (key: string) => {
    return `"Ref": {
    "type": "http",
    "url": "${import.meta.env.VITE_MCP_DOMAIN}/mcp?apiKey=${key}"
  }
}`
  }

  return (
    <PageContainer>
      <div className="api-key-section">
        <ToastContainer toasts={toasts} onRemove={removeToast} />
        <div className="section-header">
          <h2>API Keys</h2>
          {!showCreateForm && (
            <Button
              onClick={(e) => {
                if (!user?.emailVerified) {
                  showMessage('Verify your email', 3000, e.currentTarget)
                  return
                }
                setShowCreateForm(true)
              }}
              variant="primary"
              size="medium"
            >
              + New API Key
            </Button>
          )}
        </div>
        <p>
          For instructions on how to get set up, see the{' '}
          <a href="/mcp" target="_blank" rel="noopener noreferrer" className="link">
            MCP docs.
          </a>
        </p>

        {error && <div className="error-message">{error}</div>}

        {showCreateForm && (
          <CreateKeyForm
            newKeyName={newKeyName}
            setNewKeyName={setNewKeyName}
            handleCreateApiKey={handleCreateApiKey}
            isCreating={isCreating}
            setShowCreateForm={setShowCreateForm}
            inputRef={inputRef}
          />
        )}

        {newKey && anyActiveKeys && (
          <NewKeyDisplay
            newKey={newKey}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            handleCopyApiKey={handleCopyApiKey}
            getStdioConfig={getStdioConfig}
            getHttpConfig={getHttpConfig}
          />
        )}

        {!anyActiveKeys && (
          <div className="api-keys-list">
            <p className="empty-keys">You don't have any active API keys.</p>
          </div>
        )}

        <div className="api-keys-list">
          {loading ? (
            <p className="loading-text">Loading API keys...</p>
          ) : apiKeys.length === 0 ? (
            <p className="empty-keys">You don't have any API keys yet.</p>
          ) : (
            <div className="keys-container">
              {apiKeys.map((key) => (
                <div key={key.id} className="key-item">
                  <div className="key-item-header">
                    <div className="key-value-display">
                      <div className="key-item-name">
                        {key.isActive ? key.name : <s>{key.name}</s>}
                      </div>
                      <span className="key-text">
                        {visibleKeys[key.id] ? key.key : truncateKey(key.key)}
                      </span>
                      <Button
                        onClick={() => toggleKeyVisibility(key.id, key)}
                        title={visibleKeys[key.id] ? 'Hide API Key' : 'Show API Key & Setup'}
                        variant="primary"
                        size="small"
                        disabled={!key.isActive}
                      >
                        {visibleKeys[key.id] ? <EyeIcon /> : <EyeOffIcon />}
                      </Button>
                      <Button
                        onClick={(e) => handleCopyApiKey(key.key, e.currentTarget)}
                        title="Copy API Key"
                        variant="primary"
                        size="small"
                        disabled={!key.isActive}
                      >
                        <CopyIcon />
                      </Button>
                    </div>
                    <div className="key-item-actions">
                      {key.isActive ? (
                        <Button
                          onClick={() => handleRevokeApiKey(key.id)}
                          variant="danger"
                          size="small"
                        >
                          Revoke
                        </Button>
                      ) : (
                        <span className="key-status">Revoked</span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </PageContainer>
  )
}

export default ApiKeyManager
